/**
 * Strom oprávnění pro sekci Nastavení → Skupiny → Oprávnění.
 *
 * Zdroj pravdy o tom, jaké role existují, je tabulka `role` v databázi
 * (endpoint `/api/roles`). Tenhle soubor říká jen, jak se zobrazí — jaké
 * sekce, v jakém pořadí a co patří pod „Úpravy".
 *
 * Proč tady a ne v DB: každá role musí být stejně zadrátovaná v kódu na
 * místě, kde se kontroluje. Role, kterou nikdo nekontroluje, je jen ozdoba,
 * takže ukládat strom do databáze by přidalo tabulku, ne svobodu.
 *
 * Role, která je v katalogu a chybí tady, se vykreslí do sekce „Ostatní"
 * na konci — nezmizí, jen bude vidět, že se na ni zapomnělo.
 *
 * Viz plans/acl-role-plan.md.
 */

// Podřízené role přepínače „Úpravy". Ukládají se jen ony, „Úpravy" samotné
// jako role neexistují.
const UPRAVY = (prefix) => ({
  key: `${prefix}.edit`,
  label: 'Úpravy',
  children: [
    { code: `${prefix}.create`, label: 'Vytváření' },
    { code: `${prefix}.update`, label: 'Editace' },
    { code: `${prefix}.delete`, label: 'Mazání' },
  ],
});

export const PERMISSION_TREE = [
  {
    key: 'invoice',
    label: 'Fakturace',
    icon: 'bi-receipt',
    items: [
      { code: 'invoice.view', label: 'Zobrazení' },
      UPRAVY('invoice'),
      { code: 'invoice.payment_matching', label: 'Párování plateb' },
    ],
  },
  {
    key: 'order',
    label: 'Objednávky',
    icon: 'bi-cart',
    items: [{ code: 'order.view', label: 'Zobrazení' }, UPRAVY('order')],
  },
  {
    key: 'crm',
    label: 'CRM (zákazníci)',
    icon: 'bi-building',
    items: [{ code: 'crm.view', label: 'Zobrazení' }, UPRAVY('crm')],
  },
  {
    key: 'media',
    label: 'Média a ceníky',
    icon: 'bi-newspaper',
    items: [{ code: 'media.view', label: 'Zobrazení' }, UPRAVY('media')],
  },
  {
    key: 'spigl',
    label: 'Špíglování',
    icon: 'bi-layout-text-window',
    items: [
      { code: 'spigl.view', label: 'Zobrazení' },
      UPRAVY('spigl'),
      { code: 'spigl.publish', label: 'Publikovat drafty' },
    ],
  },
  // Jednoúrovňové sekce — jedna role ovládá celou sekci.
  {
    key: 'statistics',
    label: 'Statistiky',
    icon: 'bi-graph-up',
    items: [
      { code: 'statistics.view', label: 'Zobrazení' },
      { code: 'statistics.edit', label: 'Ruční zásahy' },
      { code: 'statistics.close', label: 'Uzávěrka období' },
    ],
  },
  { key: 'users', label: 'Uživatelé a role', icon: 'bi-people', single: 'users.manage' },
  { key: 'settings', label: 'Systémové nastavení', icon: 'bi-gear', single: 'settings.manage' },
];

/** Všechny kódy, které strom zná — pro rozpoznání „osiřelých" rolí v katalogu. */
export function treeCodes() {
  const out = [];
  for (const section of PERMISSION_TREE) {
    if (section.single) {
      out.push(section.single);
      continue;
    }
    for (const item of section.items) {
      if (item.code) out.push(item.code);
      else if (item.children) item.children.forEach((c) => out.push(c.code));
    }
  }
  return out;
}

/** Všechny kódy jedné sekce (i vnořené). */
export function sectionCodes(section) {
  if (section.single) return [section.single];
  const out = [];
  for (const item of section.items) {
    if (item.code) out.push(item.code);
    else if (item.children) item.children.forEach((c) => out.push(c.code));
  }
  return out;
}

/**
 * Kód „Zobrazení" sekce. Zaškrtnutí čehokoliv v sekci ho musí zapnout —
 * právo mazat fakturu, kterou nesmíš vidět, nedává smysl.
 */
export function viewCode(section) {
  if (section.single) return section.single;
  const view = section.items.find((i) => i.code && i.code.endsWith('.view'));
  return view ? view.code : null;
}
